import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
} from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Trash2, Building2, Calendar, Edit2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  getPropertyAvailabilityBlocks,
  PropertyAvailabilityBlockItemApi,
  deletePropertyAvailabilityBlock,
} from "services/propertyAvailabilityService";
import { FullScreenLoading } from "components/ui/loader";
import Pagination from "ui/pagination";
import { Input } from "ui/input";

const BlockProperty = () => {
  const [blockedProperties, setBlockedProperties] = useState<
    PropertyAvailabilityBlockItemApi[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] =
    useState<PropertyAvailabilityBlockItemApi | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadBlockedProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, debouncedSearch]);

  const loadBlockedProperties = async () => {
    try {
      setLoading(true);

      const response = await getPropertyAvailabilityBlocks({
        SearchText: debouncedSearch.trim(),
        pageNo,
        pageSize,
      });

      setBlockedProperties(response.items);

      setTotalCount(response.totalCount);
    } catch {
      toast.error("Failed to load blocked properties");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item: PropertyAvailabilityBlockItemApi) => {
    setDeletingItem(item);

    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      setLoading(true);

      await deletePropertyAvailabilityBlock(deletingItem.publicId);

      toast.success("Blocked property deleted successfully");

      setIsDeleteDialogOpen(false);

      setDeletingItem(null);

      await loadBlockedProperties();
    } catch (error) {
      console.error(error);

      toast.error("Failed to delete blocked property");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <>
        {loading && <FullScreenLoading />}
        <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />

              <Input
                placeholder="Search by property name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(1);
                }}
                className="pl-10"
              />
            </div>
          </div>
          {/* Table */}
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
            <CardContent style={{ padding: "0px 0px 10px 0px" }}>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Property Name
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Room Type
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-center"
                      >
                        Rooms
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        To Type
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Block Period
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs"
                      >
                        Remark
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-center"
                      >
                        Status
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-center"
                      >
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blockedProperties.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="text-center py-8 px-4 text-gray-500 text-sm"
                        >
                          No blocked properties found
                        </TableCell>
                      </TableRow>
                    ) : (
                      blockedProperties.map((block) => (
                        <TableRow
                          key={block.publicId}
                          className="hover:bg-gray-50"
                        >
                          <TableCell className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-green-600" />
                              <span className="font-medium text-sm">
                                {block.propertyName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <Badge variant="outline" className="text-xs">
                              {block.roomTypeName}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-center">
                            <span className="font-semibold text-sm">
                              {block.numberOfRooms}
                            </span>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {block.availableTourTypes?.map((type, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-[10px] border-blue-300 text-blue-700 px-1.5 py-0.5"
                                >
                                  {type.typeName}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="flex items-center gap-1 text-xs">
                              <Calendar className="w-3 h-3 text-gray-400" />
                              <span>
                                {new Date(
                                  block.blockStartDate,
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(
                                  block.blockEndDate,
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="max-w-xs">
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {block.remark}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3 text-center">
                            <Badge
                              className={`${
                                block.isActive
                                  ? "bg-green-100 text-green-700 border-green-300"
                                  : "bg-gray-100 text-gray-700 border-gray-300"
                              } text-[10px] px-1.5 py-0.5`}
                            >
                              {block.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  navigate(
                                    `/property-availability/block/edit/${block.publicId}`,
                                  )
                                }
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(block)}
                                className="h-8 px-2 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="block lg:hidden p-4 space-y-4">
                {blockedProperties.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No blocked properties found
                  </div>
                ) : (
                  blockedProperties.map((block) => (
                    <Card key={block.publicId} className="border-[#5B9BD5]/20">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-green-600" />
                            <span className="font-semibold text-sm">
                              {block.propertyName}
                            </span>
                          </div>
                          <Badge
                            variant="outline"
                            className={`text-xs cursor-pointer ${
                              block.isActive
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}
                          >
                            {block.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600 text-xs">
                              Room Type:
                            </span>
                            <p className="font-medium">{block.roomTypeName}</p>
                          </div>
                          <div>
                            <span className="text-gray-600 text-xs">
                              Rooms:
                            </span>
                            <p className="font-semibold">
                              {block.numberOfRooms}
                            </p>
                          </div>
                        </div>

                        <div>
                          <span className="text-gray-600 text-xs">
                            {" "}
                            TO Type:{" "}
                          </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {block.availableTourTypes?.map((type, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="text-xs border-blue-300 text-blue-700"
                              >
                                {" "}
                                {type.typeName}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div>
                          <span className="text-gray-600 text-xs">
                            Block Period:
                          </span>
                          <div className="flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="text-xs">
                              {new Date(
                                block.blockStartDate,
                              ).toLocaleDateString()}{" "}
                              -{" "}
                              {new Date(
                                block.blockEndDate,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div>
                          <span className="text-gray-600 text-xs">Remark:</span>
                          <p className="text-xs text-gray-700 mt-1">
                            {block.remark}
                          </p>
                        </div>

                        <div className="pt-2 border-t flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              navigate(
                                `/property-availability/block/edit/${block.publicId}`,
                              )
                            }
                            className="text-[#5B9BD5] hover:bg-sky-50"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(block)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
            {totalCount > pageSize && (
              <Pagination
                totalItems={totalCount}
                itemsPerPage={pageSize}
                currentPage={pageNo}
                onPageChange={(page) => setPageNo(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageNo(1);
                }}
                label="blocked properties"
              />
            )}
          </Card>
        </div>
      </>
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete Block Property
            </DialogTitle>

            <DialogDescription>
              Are you sure you want to delete block property for{" "}
              <span className="font-semibold">
                "{deletingItem?.propertyName}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingItem(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlockProperty;
